import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';

import { requireSupabase } from '@/lib/supabase/client';

const WELCOME_FLAG = 'tashkeela.welcomeNotifSent';

/**
 * Fire an immediate LOCAL notification so the user can confirm notifications
 * display on their device (permission + channel are live). The goal/full-time
 * pushes only arrive on a real match event, so this gives instant proof.
 */
export async function sendTestNotification(title: string, body: string): Promise<void> {
  await ensureAndroidChannels();
  await Notifications.scheduleNotificationAsync({
    content: { title, body, color: '#22D3FF' },
    trigger: (Platform.OS === 'android'
      ? { channelId: 'match-alerts' }
      : null) as Notifications.NotificationTriggerInput,
  });
}

/** Once per install, drop a welcome notification right after push is enabled. */
async function maybeSendWelcome(): Promise<void> {
  try {
    if (await AsyncStorage.getItem(WELCOME_FLAG)) return;
    await sendTestNotification('Tashkeela ⚽', 'Notifications are on  ·  الإشعارات اشتغلت');
    await AsyncStorage.setItem(WELCOME_FLAG, '1');
  } catch {
    // Non-fatal.
  }
}

export interface PushPrefs {
  goalNotifications: boolean;
  /** When true, goal AND full-time alerts fire only for followed teams. */
  goalFollowedOnly: boolean;
  /** Push the result when a (followed) match finishes. */
  fulltimeAlerts: boolean;
  /** Heads-up ~1h before a followed team's match kicks off (always followed-only). */
  prematchReminders: boolean;
  deadlineReminders: boolean;
  /** Post-match rating of a player you follow. */
  playerRatingAlerts: boolean;
}

/**
 * Android groups all match pushes (pre-match heads-up, goals, full-time) into
 * one high-importance channel so they read as a single live thread. No-op on
 * iOS/web. Safe to call repeatedly - creating a channel that exists is idempotent.
 */
async function ensureAndroidChannels(): Promise<void> {
  if (Platform.OS !== 'android') return;
  await Notifications.setNotificationChannelAsync('match-alerts', {
    name: 'Match alerts',
    importance: Notifications.AndroidImportance.HIGH,
    sound: 'default',
    vibrationPattern: [0, 250, 250, 250],
    lightColor: '#22D3FF',
  });
}

/**
 * Why registration can be unavailable:
 *  - web: expo-notifications has no web push support,
 *  - Expo Go (SDK 53+): remote push requires a development build,
 *  - missing EAS project id: `eas init` hasn't been run yet.
 * The UI shows a friendly hint instead of broken toggles in those cases.
 */
export type PushAvailability = 'available' | 'web' | 'needs-dev-build' | 'denied';

function easProjectId(): string | null {
  const fromExtra = Constants.expoConfig?.extra?.eas?.projectId;
  return typeof fromExtra === 'string' && fromExtra.length > 0 ? fromExtra : null;
}

export function pushAvailability(): PushAvailability {
  if (Platform.OS === 'web') return 'web';
  if (!Device.isDevice) return 'needs-dev-build';
  if (!easProjectId()) return 'needs-dev-build';
  return 'available';
}

/**
 * Ask permission, fetch the Expo push token and upsert it with the user's
 * preferences. Returns the availability outcome so the UI can explain itself.
 */
export async function registerForPush(prefs: PushPrefs): Promise<PushAvailability> {
  const availability = pushAvailability();
  if (availability !== 'available') return availability;

  const permission = await Notifications.requestPermissionsAsync();
  if (permission.status !== 'granted') return 'denied';

  await ensureAndroidChannels();

  const projectId = easProjectId();
  if (!projectId) return 'needs-dev-build';
  const token = (await Notifications.getExpoPushTokenAsync({ projectId })).data;

  const db = requireSupabase();
  const user = await db.auth.getUser();
  const userId = user.data.user?.id;
  if (!userId) throw new Error('AUTH_REQUIRED');

  const { error } = await db.from('user_push_tokens').upsert(
    {
      user_id: userId,
      token,
      platform: Platform.OS === 'ios' ? 'ios' : 'android',
      goal_notifications: prefs.goalNotifications,
      goal_followed_only: prefs.goalFollowedOnly,
      fulltime_alerts: prefs.fulltimeAlerts,
      prematch_reminders: prefs.prematchReminders,
      deadline_reminders: prefs.deadlineReminders,
      player_rating_alerts: prefs.playerRatingAlerts,
    },
    { onConflict: 'user_id,token' },
  );
  if (error) throw new Error(error.message);
  void maybeSendWelcome();
  return 'available';
}

/** Update preferences on every registered device of the signed-in user. */
export async function updatePushPrefs(prefs: PushPrefs): Promise<void> {
  const db = requireSupabase();
  const user = await db.auth.getUser();
  const userId = user.data.user?.id;
  if (!userId) throw new Error('AUTH_REQUIRED');
  const { error } = await db
    .from('user_push_tokens')
    .update({
      goal_notifications: prefs.goalNotifications,
      goal_followed_only: prefs.goalFollowedOnly,
      fulltime_alerts: prefs.fulltimeAlerts,
      prematch_reminders: prefs.prematchReminders,
      deadline_reminders: prefs.deadlineReminders,
      player_rating_alerts: prefs.playerRatingAlerts,
    })
    .eq('user_id', userId);
  if (error) throw new Error(error.message);
}

/** The boolean preference columns on `user_push_tokens` we can patch. */
type PrefColumns = {
  goal_notifications: boolean;
  goal_followed_only: boolean;
  fulltime_alerts: boolean;
  prematch_reminders: boolean;
  deadline_reminders: boolean;
  player_rating_alerts: boolean;
};

/** Map each camelCase pref onto its snake_case column on `user_push_tokens`. */
const PREF_COLUMN: Record<keyof PushPrefs, keyof PrefColumns> = {
  goalNotifications: 'goal_notifications',
  goalFollowedOnly: 'goal_followed_only',
  fulltimeAlerts: 'fulltime_alerts',
  prematchReminders: 'prematch_reminders',
  deadlineReminders: 'deadline_reminders',
  playerRatingAlerts: 'player_rating_alerts',
};

/**
 * Persist a PARTIAL preference change to every registered device row of the
 * signed-in user. The settings UI flips one switch at a time, so writing only
 * the changed column (rather than the whole prefs object) avoids clobbering a
 * pref that another device/tab changed between read and write. No-op when the
 * partial is empty. Throws AUTH_REQUIRED when signed out so the caller can
 * decide whether to surface it.
 */
export async function setMyPushPrefs(partial: Partial<PushPrefs>): Promise<void> {
  const update: Partial<PrefColumns> = {};
  for (const key of Object.keys(partial) as (keyof PushPrefs)[]) {
    const value = partial[key];
    if (typeof value === 'boolean') update[PREF_COLUMN[key]] = value;
  }
  if (Object.keys(update).length === 0) return;

  const db = requireSupabase();
  const user = await db.auth.getUser();
  const userId = user.data.user?.id;
  if (!userId) throw new Error('AUTH_REQUIRED');
  const { error } = await db.from('user_push_tokens').update(update).eq('user_id', userId);
  if (error) throw new Error(error.message);
}

/**
 * Whether the OS notification permission is currently granted. The settings
 * screen uses this to warn the user when alerts are switched on in-app but the
 * device will silently drop them (permission revoked in system settings). Web
 * has no concept of this here, so it reports `true` (don't nag). Best-effort:
 * any failure resolves to `true` rather than showing a false alarm.
 */
export async function notificationsAuthorized(): Promise<boolean> {
  if (Platform.OS === 'web') return true;
  try {
    const { status } = await Notifications.getPermissionsAsync();
    return status === 'granted';
  } catch {
    return true;
  }
}

// ---------------------------------------------------------------------------
// Match reminders - one-off LOCAL heads-up before kickoff (no server/push).
// ---------------------------------------------------------------------------

/** Fire the reminder this long before kickoff. */
const REMINDER_LEAD_MS = 10 * 60 * 1000;

function reminderId(fixtureId: string): string {
  return `match-reminder-${fixtureId}`;
}

/**
 * Schedule a local notification ~10 min before kickoff. Asks for notification
 * permission on first use. Returns false if it couldn't schedule (web,
 * permission denied, or kickoff already too close).
 *
 * Idempotent on the fixture: any existing reminder for this fixture is cancelled
 * first, so calling this again with a FRESHER kickoff_at (e.g. after a reschedule)
 * cleanly replaces the stale fire-time instead of leaving the old one behind.
 * This local path is the only second-accurate reminder and works even when the
 * server push pipeline is down.
 */
export async function scheduleMatchReminder(
  fixtureId: string,
  kickoffISO: string,
  title: string,
  body: string,
): Promise<boolean> {
  if (Platform.OS === 'web') return false;
  const fireAt = new Date(kickoffISO).getTime() - REMINDER_LEAD_MS;
  if (!Number.isFinite(fireAt) || fireAt <= Date.now() + 30_000) return false;

  const permission = await Notifications.getPermissionsAsync();
  if (permission.status !== 'granted') {
    const requested = await Notifications.requestPermissionsAsync();
    if (requested.status !== 'granted') return false;
  }
  await ensureAndroidChannels();
  // Replace any prior schedule for this fixture so a moved kickoff never leaves a
  // stale reminder firing at the old time (the identifier reuse handles most
  // platforms, but an explicit cancel is the robust path).
  await Notifications.cancelScheduledNotificationAsync(reminderId(fixtureId)).catch(() => {});
  await Notifications.scheduleNotificationAsync({
    identifier: reminderId(fixtureId),
    content: { title, body, color: '#22D3FF', data: { type: 'reminder', fixtureId } },
    trigger: {
      type: Notifications.SchedulableTriggerInputTypes.DATE,
      date: new Date(fireAt),
      channelId: 'match-alerts',
    },
  });
  return true;
}

/**
 * Best-effort: if a reminder is ALREADY scheduled for this fixture and the
 * kickoff has moved, reschedule it to the new kickoff-10min. No-op when no
 * reminder is set (we never opt the user in for them) or when the fire-time is
 * unchanged / already past. Keeps the on-device reminder fresh after a
 * reschedule without the user re-tapping. Returns true if it rescheduled.
 */
export async function refreshMatchReminderIfScheduled(
  fixtureId: string,
  kickoffISO: string,
  title: string,
  body: string,
): Promise<boolean> {
  if (Platform.OS === 'web') return false;
  const id = reminderId(fixtureId);
  const all = await Notifications.getAllScheduledNotificationsAsync();
  const existing = all.find((n) => n.identifier === id);
  if (!existing) return false; // not opted in — leave it alone

  const fireAt = new Date(kickoffISO).getTime() - REMINDER_LEAD_MS;
  if (!Number.isFinite(fireAt) || fireAt <= Date.now() + 30_000) return false;

  // Skip if the trigger already fires at (about) the right time.
  const trigger = existing.trigger as { type?: string; value?: number } | null;
  const currentMs = typeof trigger?.value === 'number' ? trigger.value : null;
  if (currentMs != null && Math.abs(currentMs - fireAt) < 60_000) return false;

  await ensureAndroidChannels();
  await Notifications.cancelScheduledNotificationAsync(id).catch(() => {});
  await Notifications.scheduleNotificationAsync({
    identifier: id,
    content: { title, body, color: '#22D3FF', data: { type: 'reminder', fixtureId } },
    trigger: {
      type: Notifications.SchedulableTriggerInputTypes.DATE,
      date: new Date(fireAt),
      channelId: 'match-alerts',
    },
  });
  return true;
}

/** Cancel a previously scheduled match reminder (no-op if none). */
export async function cancelMatchReminder(fixtureId: string): Promise<void> {
  if (Platform.OS === 'web') return;
  await Notifications.cancelScheduledNotificationAsync(reminderId(fixtureId));
}

/** Whether a reminder is currently scheduled for this fixture. */
export async function hasMatchReminder(fixtureId: string): Promise<boolean> {
  if (Platform.OS === 'web') return false;
  const all = await Notifications.getAllScheduledNotificationsAsync();
  return all.some((n) => n.identifier === reminderId(fixtureId));
}

// ---------------------------------------------------------------------------
// Deep-link notification taps -> the relevant match (or the Today hub).
// ---------------------------------------------------------------------------

/**
 * fixtureId must be a plain UUID. Validating it (rather than interpolating an
 * arbitrary string into the path) stops a crafted / relayed push payload from
 * steering expo-router to an unintended route or smuggling query/`..` segments
 * via `/match/${id}`.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The notification request identifier we last routed for. On a cold launch the
 * SAME tapped notification reaches BOTH getLastNotificationResponseAsync AND the
 * warm addNotificationResponseReceivedListener, which would double-navigate to
 * the same match. We dedup by request id so the second delivery is a no-op. A
 * later genuine tap of the SAME notification (rare) still has the same id and is
 * intentionally ignored — re-opening a screen you're already on adds nothing.
 */
let lastHandledNotificationId: string | null = null;

function routeFromNotification(
  data: Record<string, unknown> | undefined,
  navigate: (path: string) => void,
  requestId?: string | null,
): void {
  if (!data) return;
  // Dedup the cold-start double-delivery (same request id via both paths).
  if (requestId) {
    if (lastHandledNotificationId === requestId) return;
    lastHandledNotificationId = requestId;
  }
  if (typeof data.fixtureId === 'string' && UUID_RE.test(data.fixtureId)) {
    navigate(`/match/${data.fixtureId}`);
  } else if (data.type === 'digest') {
    navigate('/today');
  }
}

/**
 * Route a tapped notification to the right place: a goal / lineup / pick-hit
 * push carries a fixtureId (-> the match), the morning digest opens the Today
 * hub. Handles a warm tap (listener) AND a cold start (the notification that
 * launched the app). Returns a cleanup. Web-safe no-op.
 *
 * `ready` gates the COLD-START navigate: on a cold launch
 * `getLastNotificationResponseAsync` resolves before the expo-router Stack is
 * mounted, so navigating immediately steers a router that isn't there yet and
 * the app opens to a blank (black) screen. We hold the cold-start path until the
 * caller signals the router is mounted, then flush it once. Warm taps (the app
 * already running) navigate straight away — the Stack is guaranteed mounted.
 */
export function listenForNotificationTaps(
  navigate: (path: string) => void,
  ready = true,
): () => void {
  if (Platform.OS === 'web') return () => {};

  // Warm taps (app already running) navigate immediately — the Stack is mounted.
  const sub = Notifications.addNotificationResponseReceivedListener((resp) => {
    routeFromNotification(
      resp.notification.request.content.data as Record<string, unknown> | undefined,
      navigate,
      resp.notification.request.identifier,
    );
  });

  // Cold start: the launching notification. Only consume it once the router is
  // mounted (`ready`). When called before the app is ready, do NOTHING here — the
  // layout re-invokes this with `ready === true` after mount and we flush then,
  // so navigating never races an unmounted Stack (the black-screen cause).
  if (ready) {
    void Notifications.getLastNotificationResponseAsync()
      .then((resp) => {
        routeFromNotification(
          resp?.notification.request.content.data as Record<string, unknown> | undefined,
          navigate,
          resp?.notification.request.identifier,
        );
      })
      .catch(() => {});
  }

  return () => sub.remove();
}

/** The user's stored preferences (from any device row), or defaults. */
export async function getMyPushPrefs(): Promise<PushPrefs & { registered: boolean }> {
  const db = requireSupabase();
  const { data, error } = await db
    .from('user_push_tokens')
    .select(
      'goal_notifications, goal_followed_only, fulltime_alerts, prematch_reminders, deadline_reminders, player_rating_alerts',
    )
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) {
    return {
      goalNotifications: true,
      goalFollowedOnly: false,
      fulltimeAlerts: true,
      prematchReminders: true,
      deadlineReminders: true,
      playerRatingAlerts: true,
      registered: false,
    };
  }
  return {
    goalNotifications: data.goal_notifications,
    goalFollowedOnly: data.goal_followed_only,
    fulltimeAlerts: data.fulltime_alerts,
    prematchReminders: data.prematch_reminders,
    deadlineReminders: data.deadline_reminders,
    playerRatingAlerts: data.player_rating_alerts ?? true,
    registered: true,
  };
}
